import React from 'react';
import { Shield } from 'lucide-react';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const PrivacyPolicy = () => {
  return (
    <>
      <LandingNav/>
      <div className="font-inter bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 min-h-screen">
        <div className="max-w-5xl mx-auto bg-white p-8 md:p-12 rounded-2xl shadow-2xl"> 
          <div className="text-center mb-12">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-lg text-gray-600">Last Updated: January 2025</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8 font-semibold">
              At Manocore Technologies ("Manocore," "we," "us," or "our"), we are committed to protecting your privacy and maintaining the security of your personal information. This Privacy Policy explains how we collect, use, process, and protect your data when you use our Shiftry workforce management platform (the "Service").
            </p>

            {/* Section 1: Information Collection */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">1. Information We Collect</h2>
              
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">1.1 Account Information</h3>
              <p className="mb-4 text-gray-700 leading-relaxed">
                When you register for a Shiftry account, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
                <li>Full name, email address, and phone number</li>
                <li>Organization name, industry, and size</li>
                <li>Job title and department</li>
                <li>Password (encrypted and securely stored)</li>
                <li>Billing and payment information (processed through Razorpay)</li>
              </ul>

              <h3 className="text-2xl font-semibold text-gray-800 mb-4">1.2 Employee Data</h3>
              <p className="mb-4 text-gray-700 leading-relaxed">
                Employers using Shiftry may input workforce data including:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
                <li>Employee names, contact information, and identification numbers</li>
                <li>Work schedules, shift assignments, and time-off requests</li>
                <li>Clock-in/out records with GPS location data (when enabled)</li>
                <li>Performance metrics and attendance records</li>
                <li>Payroll information and compensation details</li>
                <li>Skills, certifications, and training records</li>
              </ul>

              <h3 className="text-2xl font-semibold text-gray-800 mb-4">1.3 Automatically Collected Data</h3>
              <p className="mb-4 text-gray-700 leading-relaxed">
                We automatically collect technical and usage information:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
                <li>IP addresses, device identifiers, and browser types</li>
                <li>Operating system and device model information</li>
                <li>Pages visited, features used, and time spent on the platform</li>
                <li>GPS location data (with explicit permission for geo-fencing features)</li>
                <li>Cookies, web beacons, and similar tracking technologies</li>
              </ul>
            </section>

            {/* Section 2: How We Use Information */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">2. How We Use Your Information</h2>
              <p className="mb-4 text-gray-700 leading-relaxed">
                We use collected data for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-gray-700 ml-6">
                <li><strong>Service Delivery:</strong> Provide and maintain the Shiftry platform, including scheduling, time tracking, and analytics</li>
                <li><strong>Account Management:</strong> Create and manage user accounts, process subscriptions, and handle billing</li>
                <li><strong>Customer Support:</strong> Respond to inquiries, troubleshoot issues, and provide technical assistance</li>
                <li><strong>Product Improvement:</strong> Analyze usage patterns to enhance features, performance, and user experience</li>
                <li><strong>Communications:</strong> Send service updates, security alerts, and important announcements</li>
                <li><strong>Compliance:</strong> Meet legal obligations, enforce our Terms of Service, and protect against fraud</li>
                <li><strong>AI Features:</strong> Power AI-driven scheduling optimization and predictive analytics (with your consent)</li>
              </ul>
            </section>

            {/* Section 3: Subscription and Cancellation */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">3. Subscription Services & Cancellation</h2>
              
              <h3 className="text-2xl font-semibold text-gray-800 mb-4">3.1 Subscription Model</h3>
              <p className="mb-4 text-gray-700 leading-relaxed">
                Shiftry operates on a subscription basis with the following plans:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
                <li>Monthly subscriptions (auto-renew monthly)</li>
                <li>Annual subscriptions (auto-renew yearly with discounted rates)</li>
                <li>Enterprise custom plans (negotiated terms)</li>
              </ul>

              <h3 className="text-2xl font-semibold text-gray-800 mb-4">3.2 Auto-Renewal</h3>
              <p className="mb-4 text-gray-700 leading-relaxed">
                Subscriptions automatically renew at the end of each billing period unless you cancel. You will be charged through Razorpay using your saved payment method. We will notify you 7 days before renewal.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-4">3.3 Cancel Anytime Policy</h3>
              <p className="mb-4 text-gray-700 leading-relaxed">
                <strong>You can cancel your subscription at any time with no penalties or cancellation fees.</strong> To cancel:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
                <li>Log into your account and navigate to Settings → Billing</li>
                <li>Click "Cancel Subscription" and confirm</li>
                <li>Contact support@manocore.com for assistance</li>
              </ul>
              <p className="mb-6 text-gray-700 leading-relaxed">
                Upon cancellation, you retain access until the end of your current billing period. No refunds are provided for partial periods, but you won't be charged again after cancellation.
              </p>

              <h3 className="text-2xl font-semibold text-gray-800 mb-4">3.4 Data Retention After Cancellation</h3>
              <p className="mb-6 text-gray-700 leading-relaxed">
                After cancellation, your data is retained for 90 days in case you wish to reactivate. After 90 days, all data is permanently deleted unless required by law. You can request immediate data deletion by contacting support@manocore.com.
              </p>
            </section>

            {/* Section 4: Data Sharing */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">4. Data Sharing and Disclosure</h2>
              <p className="mb-4 text-gray-700 leading-relaxed">
                We do not sell your personal data. We may share information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-gray-700 ml-6">
                <li><strong>Service Providers:</strong> Razorpay (payment processing), AWS (cloud hosting), third-party analytics tools</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize data sharing with third-party integrations</li>
              </ul>
            </section>

            {/* Section 5: Data Security */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">5. Data Security</h2>
              <p className="mb-4 text-gray-700 leading-relaxed">
                We implement enterprise-grade security measures to protect your data:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
                <li>256-bit SSL/TLS encryption for data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Multi-factor authentication (MFA) support</li>
                <li>Regular security audits and penetration testing</li>
                <li>ISO 27001 certified data centers</li>
                <li>Role-based access controls (RBAC)</li>
                <li>Automated backup systems with disaster recovery</li>
              </ul>
            </section>

            {/* Section 6: GDPR Compliance */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">6. GDPR and Data Privacy Rights</h2>
              <p className="mb-4 text-gray-700 leading-relaxed">
                If you are located in the European Economic Area (EEA) or India, you have specific rights under GDPR and local data protection laws:
              </p>
              <ul className="list-disc list-inside space-y-3 mb-6 text-gray-700 ml-6">
                <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Portability:</strong> Export your data in a machine-readable format</li>
                <li><strong>Right to Object:</strong> Opt out of certain data processing activities</li>
                <li><strong>Right to Restriction:</strong> Limit how we process your data</li>
              </ul>
              <p className="mb-6 text-gray-700 leading-relaxed">
                To exercise these rights, contact us at legal@manocore.com. We will respond within 30 days.
              </p>
            </section>

            {/* Section 7: Cookies */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">7. Cookies and Tracking Technologies</h2>
              <p className="mb-4 text-gray-700 leading-relaxed">
                We use cookies and similar technologies for:
              </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-700 ml-6">
                <li><strong>Essential Cookies:</strong> Required for login, security, and core functionality</li>
                <li><strong>Performance Cookies:</strong> Analyze usage patterns and improve performance</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Google Analytics for aggregated usage statistics</li>
              </ul>
              <p className="mb-6 text-gray-700 leading-relaxed">
                You can manage cookie preferences through your browser settings. Disabling essential cookies may limit platform functionality.
              </p>
            </section>

            {/* Section 8: Data Transfers */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">8. International Data Transfers</h2>
              <p className="mb-6 text-gray-700 leading-relaxed">
                Your data may be processed in India, the United States, or other countries where our service providers operate. We ensure adequate safeguards through Standard Contractual Clauses (SCCs) and comply with applicable data transfer regulations.
              </p>
            </section>

            {/* Section 9: Children's Privacy */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">9. Children's Privacy</h2>
              <p className="mb-6 text-gray-700 leading-relaxed">
                Shiftry is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we discover such data, we will delete it immediately.
              </p>
            </section>

            {/* Section 10: Changes to Policy */}
            <section className="mb-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">10. Policy Updates</h2>
              <p className="mb-6 text-gray-700 leading-relaxed">
                We may update this Privacy Policy periodically. We will notify you of significant changes via email or in-app notification. Continued use of the Service after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="mb-4 text-gray-700 leading-relaxed">
                For privacy-related questions or to exercise your rights:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> legal@manocore.com</p>
                <p><strong>Support:</strong> support@manocore.com</p>
                <p><strong>Address:</strong> Manocore Technologies Private Limited, Chennai, India</p>
              </div>
            </section>

            <div className="text-center pt-8 border-t-2 border-gray-200">
              <p className="text-sm text-gray-600">
                © 2026 Manocore Technologies. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
      <FooterLanding/>
    </>
  );
};

export default PrivacyPolicy;
